import { useEffect, useState, useRef } from "react";

function ImageCarouselModal({ photos = [], initialIndex = 0, onClose }) {
  const [index, setIndex] = useState(initialIndex);
  const [lazyLoaded, setLazyLoaded] = useState({});
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Handle swipe gestures
  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    handleSwipe();
  };

  const handleSwipe = () => {
    const swipeThreshold = 50; // Minimum distance to trigger swipe
    const distance = touchStartX.current - touchEndX.current;
    
    if (Math.abs(distance) > swipeThreshold) {
      if (distance > 0) {
        // Swiped left, go to next photo
        setIndex((i) => (i + 1) % photos.length);
      } else {
        // Swiped right, go to previous photo
        setIndex((i) => (i - 1 + photos.length) % photos.length);
      }
    }
  };

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % photos.length);
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + photos.length) % photos.length);
      if (e.key === 'Escape') onClose();
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photos.length, onClose]);

  // Lazy load thumbnails on mount and index change
  useEffect(() => {
    // Preload current and adjacent thumbnails
    const indicesToLoad = [
      index,
      (index - 1 + photos.length) % photos.length,
      (index + 1) % photos.length
    ];
    
    indicesToLoad.forEach((i) => {
      if (!lazyLoaded[i]) {
        setLazyLoaded((prev) => ({ ...prev, [i]: true }));
      }
    });
  }, [index, photos.length, lazyLoaded]);

  if (!photos || photos.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={() => onClose()}
    >
      <div
        className="relative max-w-4xl w-full mx-4"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <button onClick={onClose} aria-label="Close gallery" className="absolute right-3 top-3 bg-white/80 text-black rounded-full p-2 text-2xl z-10">✕</button>

        <div className="relative overflow-hidden rounded-lg bg-black">
          <img src={photos[index]} alt={`photo-${index}`} className="w-full h-[60vh] object-contain bg-black" />

          <button
            onClick={() => setIndex((i) => (i - 1 + photos.length) % photos.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-2 rounded-full transition"
          >◀</button>

          <button
            onClick={() => setIndex((i) => (i + 1) % photos.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-2 rounded-full transition"
          >▶</button>
        </div>

        {/* Thumbnails / scrollable row with lazy loading */}
        <div className="flex gap-2 mt-3 overflow-x-auto py-2">
          {photos.map((p, i) => (
            <div key={i} className="flex-shrink-0">
              {lazyLoaded[i] ? (
                <img
                  src={p}
                  alt={`thumb-${i}`}
                  onClick={() => setIndex(i)}
                  className={`h-16 w-24 object-cover rounded-md cursor-pointer border-2 transition ${i === index ? 'border-white' : 'border-transparent'}`}
                />
              ) : (
                <div className="h-16 w-24 object-cover rounded-md bg-gray-700 border-2 border-transparent" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ImageCarouselModal;
